// The website's privacy-friendly counter (G-AIW-14, decided by Jacky 12 October 2026: "Use a
// privacy-friendly counter" — no consent banner, anonymous page totals only).
//
// ONE definition of what a website event may carry, shared by the website's server route
// (apps/web/src/pages/api/web-event.ts), the API door (services/api POST /api/web-event) and the
// backbone insert (POST /web-events). Each layer validates again with the same schema, so nothing a
// browser invents reaches marketing.web_event.
//
// WHAT IS NEVER HERE, BY CONSTRUCTION: no cookie, no visitor or session id, no IP address, no user
// agent, no full referrer URL (only its host), no query string, no free text. `props` carries only
// whitelisted keys per event, and only numbers or booleans — a string cannot get in, so a name, an
// email, a phone or an address cannot either. A path segment that looks like an id or a personal
// link token is masked before it is stored.
import { z } from "zod";
import { sydneyDay } from "./crew-calendar.js";
export const WEB_EVENTS = ["page_view", "book_submitted", "self_quote_carried", "member_link_requested"];
export const WEB_DEVICES = ["phone", "tablet", "desktop"];
/** The props each event may carry: key → kind. Anything else is dropped, never stored. */
export const WEB_EVENT_PROPS = {
    page_view: {},
    book_submitted: { with_estimate: "boolean" },
    self_quote_carried: { m2: "number", rooms: "number", floors: "number" },
    member_link_requested: {},
};
/** The site's own hosts: a visit from one of our pages to another is not a referral. */
export const OWN_HOSTS = ["artemisflooring.com", "artemisio.com.au"];
/** Device class from the viewport width alone (CSS pixels) — the one thing about the device kept. */
export function deviceClass(width) {
    if (typeof width !== "number" || !Number.isFinite(width) || width <= 0)
        return null;
    return width < 768 ? "phone" : width < 1024 ? "tablet" : "desktop";
}
/** The referring site's host only — never its path or query — or null for a direct visit or one of our own pages. */
export function referrerHost(referrer, ownHosts = OWN_HOSTS) {
    if (!referrer)
        return null;
    let host;
    try {
        host = new URL(referrer).hostname.toLowerCase();
    }
    catch {
        return null;
    }
    host = host.replace(/^www\./, "").replace(/\.$/, "");
    if (!host || host.length > 253 || !/^[a-z0-9.-]+$/.test(host))
        return null;
    if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host))
        return null;
    if (ownHosts.some((own) => host === own || host.endsWith(`.${own}`)))
        return null;
    return host;
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A path as counted: no query, no hash, ids and personal link tokens masked, at most 200 characters. */
export function countedPath(raw) {
    if (typeof raw !== "string")
        return null;
    let path = raw.split(/[?#]/)[0].trim();
    if (!path.startsWith("/") || path.startsWith("//"))
        return null;
    try {
        path = decodeURIComponent(path);
    }
    catch { /* keep as sent */ }
    const segments = path.split("/").map((s) => {
        if (!s)
            return s;
        if (UUID.test(s))
            return ":id";
        if (s.includes("@"))
            return ":hidden";
        // a booking / proposal / member link token, or any long opaque segment
        if (s.length >= 20 && /^[A-Za-z0-9_-]+$/.test(s) && /\d/.test(s))
            return ":token";
        if (/^\d{5,}$/.test(s))
            return ":id";
        return s.toLowerCase();
    });
    const out = segments.join("/").replace(/\/{2,}/g, "/");
    return out.length > 200 ? out.slice(0, 200) : out;
}
/** Known automated visitors. Matched against the user agent on the website's server and then forgotten. */
const BOT_UA = /bot\b|bot\/|crawl|spider|slurp|facebookexternalhit|embedly|preview|headless|lighthouse|pagespeed|gtmetrix|pingdom|uptime|monitor|curl\/|wget|python-requests|python-urllib|httpclient|axios\/|node-fetch|go-http-client|java\/|okhttp|scrapy|phantomjs|selenium|puppeteer|playwright/i;
export function isBotUserAgent(ua) {
    if (!ua || ua.trim().length < 10)
        return true; // a real browser always says who it is
    return BOT_UA.test(ua);
}
/** What the browser sends (and what the website's server forwards). */
export const WebEventInput = z.object({
    event: z.enum(WEB_EVENTS),
    path: z.string().max(400),
    referrer_host: z.string().max(253).nullable().optional(),
    device: z.enum(WEB_DEVICES).nullable().optional(),
    props: z.record(z.string().max(40), z.union([z.number(), z.boolean(), z.string(), z.null()])).optional(),
}).strip();
export const WebEventRowSchema = z.object({
    occurred_at: z.iso.datetime({ offset: true }),
    sydney_day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    event: z.enum(WEB_EVENTS),
    path: z.string().min(1).max(200).startsWith("/"),
    referrer_host: z.string().max(253).regex(/^[a-z0-9.-]+$/).nullable(),
    device: z.enum(WEB_DEVICES).nullable(),
    props: z.record(z.string().max(40), z.union([z.number(), z.boolean()])).refine((p) => Object.keys(p).length <= 6, "too many props"),
}).strict();
function cleanProps(event, raw) {
    const allowed = WEB_EVENT_PROPS[event];
    const out = {};
    for (const [key, kind] of Object.entries(allowed)) {
        const v = raw?.[key];
        if (kind === "boolean" && typeof v === "boolean")
            out[key] = v;
        // numbers are counts and areas: bounded, rounded to one decimal, never an identifier
        if (kind === "number" && typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 100_000)
            out[key] = Math.round(v * 10) / 10;
    }
    return out;
}
/** Validate and reduce a browser event to exactly what is stored. Pure; `now` is the receipt time. */
export function normaliseWebEvent(raw, now = new Date()) {
    const parsed = WebEventInput.safeParse(raw);
    if (!parsed.success)
        return { ok: false, reason: parsed.error.issues.map((i) => `${i.path.join(".") || "event"}: ${i.message}`).join("; ") };
    const e = parsed.data;
    const path = countedPath(e.path);
    if (!path)
        return { ok: false, reason: "path must be a site path starting with /" };
    // the host is re-derived, so a full URL sent as referrer_host is reduced, and our own host is dropped
    const ref = e.referrer_host ? referrerHost(/^https?:\/\//i.test(e.referrer_host) ? e.referrer_host : `https://${e.referrer_host}`) : null;
    return {
        ok: true,
        row: {
            occurred_at: now.toISOString(),
            sydney_day: sydneyDay(now),
            event: e.event,
            path,
            referrer_host: ref,
            device: e.device ?? null,
            props: cleanProps(e.event, e.props ?? undefined),
        },
    };
}
//# sourceMappingURL=web-event.js.map